import React, { useState, lazy, Suspense, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Paper,
  Avatar,
  Rating,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha
} from '@mui/material';
import {
  PlayArrow,
  ArrowForward,
  CheckCircle,
  Star,
  LocationOn,
  Schedule,
  Security,
  Speed,
  Support,
  Analytics,
  AutoFixHigh,
  ExpandMore,
  Phone,
  Email,
  ChatBubble,
  Rocket,
  TrendingUp,
  Group,
  Handyman,
  Receipt,
  Event,
  Payment,
  QueryStats,
  CalendarMonth
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";
import DemoVideo from './DemoVideo';
import { metiers } from '../data/metiers';

const AccueilComplet = () => {
  // ...existing code...
  // Suppression de toute logique de notification ou d'alerte sur la page d'accueil

  const theme = useTheme();
  const navigate = useNavigate();
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [showAllMetiers, setShowAllMetiers] = useState(false);
  const [prefetchedRoutes, setPrefetchedRoutes] = useState(new Set());
  const cardRefs = useRef({});

  // Génération dynamique des métiers depuis metiers.js avec B2B activé
  const tousLesMetiers = Object.values(metiers)
    .filter(metier => metier.b2bEnabled === true)
    .map(metier => ({
      nom: metier.nom,
      icon: getMetierEmoji(metier.id),
      gradient: `linear-gradient(135deg, ${metier.couleurMetier} 0%, ${adjustColor(metier.couleurMetier, -20)} 100%)`,
      stats: metier.stats?.utilisateurs ? `${metier.stats.utilisateurs} professionnels` : '',
      description: metier.hero?.soustitre?.substring(0, 50) || `Solution ${metier.nom}`,
      id: metier.id
    }));

  // Fonction pour obtenir l'emoji du métier
  function getMetierEmoji(metierId) {
    const emojis = {
      electriciens: '💡', plombiers: '🔧', menuisiers: '🪚', macons: '🧱',
      carreleurs: '🏺', couvreurs: '🏠', peintres: '🎨', jardiniers: '🌳',
      ramoneurs: '🧹', garagistes: '🚗', controleTechnique: '🔍',
      menage: '🧽', coiffeurs: '💇', estheticiennes: '💅', masseurs: '💆',
      fleuristes: '💐', carrossiers: '🚙'
    };
    return emojis[metierId] || '🔨';
  }

  // Fonction pour ajuster la couleur (pour le gradient)
  function adjustColor(color, amount) {
    const clamp = (val) => Math.min(Math.max(val, 0), 255);
    const num = parseInt(color.replace('#', ''), 16);
    const r = clamp((num >> 16) + amount);
    const g = clamp(((num >> 8) & 0x00FF) + amount);
    const b = clamp((num & 0x0000FF) + amount);
    return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
  }

  // Métiers les plus populaires pour affichage prioritaire
  const metiersPopulaires = tousLesMetiers.slice(0, 6);

  // Fonction utilitaire pour générer le slug métier
  const getMetierSlug = (nom) => {
    return nom
      .toLowerCase()
      .replace(/é/g, 'e')
      .replace(/è/g, 'e')
      .replace(/ê/g, 'e')
      .replace(/à/g, 'a')
      .replace(/ç/g, 'c')
      .replace(/ /g, '-')
      .replace(/'/g, '')
      .replace(/[^a-z0-9-]/g, '');
  };

  // Prefetch intelligent au survol avec IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const slug = entry.target.dataset.metierSlug;
            if (slug && !prefetchedRoutes.has(slug)) {
              // Prefetch du composant MetierB2B
              const link = document.createElement('link');
              link.rel = 'prefetch';
              link.href = `/m/${slug}/b2b`;
              document.head.appendChild(link);
              setPrefetchedRoutes(prev => new Set([...prev, slug]));
            }
          }
        });
      },
      { rootMargin: '50px' }
    );

    Object.values(cardRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [prefetchedRoutes]);

  // Fonction de prefetch au survol (backup)
  const handleMetierHover = (nom) => {
    const slug = getMetierSlug(nom);
    if (!prefetchedRoutes.has(slug)) {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = `/m/${slug}/b2b`;
      document.head.appendChild(link);
      setPrefetchedRoutes(prev => new Set([...prev, slug]));
    }
  };

  // Fonctionnalités clés
  const fonctionnalites = [
    {
      icon: <AutoFixHigh sx={{ fontSize: 40, color: '#4f46e5' }} />,
      titre: "IA Intégrée",
      description: "ChatBot intelligent, recherche sémantique et prédiction de coûts avec IA avancée",
      couleur: '#4f46e5'
    },
    {
      icon: <Schedule sx={{ fontSize: 40, color: '#059669' }} />,
      titre: "Planning Automatisé",
      description: "Gestion intelligente des rendez-vous avec synchronisation temps réel",
      couleur: '#059669'
    },
    {
      icon: <Analytics sx={{ fontSize: 40, color: '#dc2626' }} />,
      titre: "Analytics Avancées",
      description: "Tableaux de bord complets avec insights business en temps réel",
      couleur: '#dc2626'
    },
    {
      icon: <Security sx={{ fontSize: 40, color: '#7c3aed' }} />,
      titre: "Paiements Sécurisés",
      description: "Integration Stripe complète avec gestion des abonnements",
      couleur: '#7c3aed'
    },
    {
      icon: <Support sx={{ fontSize: 40, color: '#ea580c' }} />,
      titre: "Support 24/7",
      description: "Assistance technique dédiée et formation personnalisée",
      couleur: '#ea580c'
    },
    {
      icon: <Speed sx={{ fontSize: 40, color: '#0891b2' }} />,
      titre: "Performance",
      description: "Interface ultra-rapide avec synchronisation en temps réel",
      couleur: '#0891b2'
    }
  ];

  // Témoignages
  const testimonials = [
    {
      nom: "Marie Dupont",
      metier: "Plombière",
      avatar: "/avatars/marie.jpg",
      note: 5,
      commentaire: "Cette plateforme a révolutionné mon business ! +60% de clients en 3 mois.",
      ville: "Paris"
    },
    {
      nom: "Pierre Martin",
      metier: "Électricien", 
      avatar: "/avatars/pierre.jpg",
      note: 5,
      commentaire: "L'IA pour les devis automatiques est incroyable. Je gagne 5h par semaine !",
      ville: "Lyon"
    },
    {
      nom: "Sophie Chen",
      metier: "Coiffeuse",
      avatar: "/avatars/sophie.jpg", 
      note: 5,
      commentaire: "Mes clients adorent la réservation en ligne. Mon salon ne désemplit plus !",
      ville: "Marseille"
    }
  ];

  const steps = [
    {
      icon: <Receipt sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: '1. Devis en quelques clics',
      description: 'Créez et envoyez des devis professionnels qui impressionnent vos clients et se transforment en factures.',
    },
    {
      icon: <Event sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: '2. Planifiez vos chantiers',
      description: 'Organisez vos rendez-vous, assignez vos équipes et suivez l’avancement de vos projets en temps réel.',
    },
    {
      icon: <Handyman sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: '3. Facturez et soyez payé',
      description: 'Générez des factures conformes, acceptez les paiements en ligne et relancez automatiquement les impayés.',
    },
  ];

  const features = [
    {
      icon: <QueryStats sx={{ fontSize: 50, color: 'secondary.main' }} />,
      title: 'Devis & Facturation IA',
      description: 'Générez des devis précis en un instant et convertissez-les en factures en un clic.',
      color: 'secondary.main'
    },
    {
      icon: <CalendarMonth sx={{ fontSize: 50, color: 'success.main' }} />,
      title: 'Planning Intelligent',
      description: 'Optimisez vos tournées et gérez vos équipes sur le terrain en temps réel.',
      color: 'success.main'
    },
    {
      icon: <Payment sx={{ fontSize: 50, color: 'error.main' }} />,
      title: 'Paiements Sécurisés',
      description: 'Encaissez rapidement grâce aux paiements en ligne. Cartes, virements, prélèvements.',
      color: 'error.main'
    },
  ];

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
    responsive: [
      {
        breakpoint: 9999,
        settings: {
          slidesToShow: 1,
          arrows: false
        }
      }
    ]
  };

  return (
    <>
      {/* HERO SECTION V2 - MODERNE & ENGAGEANT */}
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Box
          sx={{
            minHeight: '100vh',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.secondary.main, 0.9)} 100%)`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: { xs: 2, md: 2 },
            py: { xs: 4, md: 8 },
          }}
        >
          <Container maxWidth="md">
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.05, 1],
                  rotate: [0, -2, 2, 0]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <Chip 
                  label="Pour les artisans qui visent l'excellence" 
                  color="secondary" 
                  sx={{ mb: 3, bgcolor: alpha('#ffffff', 0.15), color: 'white', fontWeight: 'bold' }} 
                />
              </motion.div>
              <Typography variant="h1" fontWeight="bold" sx={{ mb: 3, fontSize: { xs: '1.5rem', sm: '2rem', md: '4rem' }, lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 } }}>
                La plateforme tout-en-un pour les artisans de demain.
              </Typography>
              <Typography variant="h5" sx={{ mb: 5, opacity: 0.9, maxWidth: '700px', mx: 'auto', fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' }, lineHeight: { xs: 1.3, md: 1.5 } }}>
                Gérez vos clients, vos devis et votre facturation avec une facilité déconcertante. Libérez votre temps pour ce qui compte vraiment : votre métier.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <motion.div 
                  whileHover={{ scale: 1.15, rotate: -3 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    onClick={() => navigate('/inscription')}
                    sx={{
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      fontWeight: 'bold',
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      boxShadow: 2,
                      transition: 'all 0.3s',
                      '&:hover': {
                        bgcolor: '#f8fafc',
                        boxShadow: 4
                      }
                    }}
                    startIcon={<Rocket />}
                  >
                    Démarrer gratuitement
                  </Button>
                </motion.div>
                  <motion.div 
                    whileHover={{ scale: 1.15, rotate: 3 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/stocks')}
                      sx={{
                        borderColor: 'white',
                        color: 'white',
                        fontWeight: 'bold',
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        boxShadow: 2,
                        transition: 'all 0.3s',
                        '&:hover': {
                          bgcolor: alpha('#ffffff', 0.1),
                          boxShadow: 4
                        }
                      }}
                      startIcon={<Handyman />}
                    >
                      Gestion des Stocks
                    </Button>
                  </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.15, rotate: 3 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/demo')}
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      fontWeight: 'bold',
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      boxShadow: 2,
                      transition: 'all 0.3s',
                      '&:hover': {
                        bgcolor: alpha('#ffffff', 0.1),
                        boxShadow: 4
                      }
                    }}
                    startIcon={<Phone />}
                  >
                    Demander une démo
                  </Button>
                </motion.div>
              </Box>
              <Typography variant="body2" sx={{ mt: 3, opacity: 0.8 }}>
                Aucune carte de crédit requise • Configuration en 5 minutes • Support gratuit
              </Typography>
            </motion.div>
          </Container>
        </Box>
      </motion.div>

      {/* SECTION "COMMENT ÇA MARCHE ?" */}
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.2 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Box sx={{ py: { xs: 3, md: 10 }, bgcolor: 'grey.50' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 0 } }}>
              <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, lineHeight: 1.2 }}>
                Simplifiez votre quotidien en 3 étapes
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }, lineHeight: 1.4 }}>
                Conçu par des artisans, pour des artisans. Reprenez le contrôle de votre temps et de votre croissance.
              </Typography>
            </Box>
            <Slider {...sliderSettings}>
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.7, rotateY: -45 }}
                  whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05, y: -10 }}
                >
                  <Box sx={{ px: 2 }}>
                    <Paper elevation={2} sx={{ 
                      p: { xs: 3, md: 4 }, 
                      borderRadius: 3, 
                      textAlign: 'center', 
                      height: '100%',
                      minHeight: { xs: '300px', md: 'auto' }
                    }}>
                      <motion.div
                        animate={{ 
                          y: [0, -10, 0],
                          rotate: [0, 5, -5, 0]
                        }}
                        transition={{ 
                          duration: 2.5,
                          repeat: Infinity,
                          repeatType: "reverse",
                          delay: index * 0.3
                        }}
                      >
                        <Box sx={{ mb: 3 }}>
                          {step.icon}
                        </Box>
                      </motion.div>
                      <Typography variant="h5" fontWeight="bold" sx={{ 
                        mb: 2, 
                        fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.5rem' }, 
                        lineHeight: 1.3 
                      }}>
                        {step.title}
                      </Typography>
                      <Typography color="text.secondary" sx={{ 
                        fontSize: { xs: '0.95rem', sm: '1rem', md: '1rem' },
                        lineHeight: 1.6
                      }}>
                        {step.description}
                      </Typography>
                    </Paper>
                  </Box>
                </motion.div>
              ))}
            </Slider>
          </Container>
        </Box>
      </motion.div>

      {/* SECTION FONCTIONNALITÉS */}
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.3 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Box sx={{ py: { xs: 3, md: 10 } }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 0 } }}>
              <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, lineHeight: 1.2 }}>
                Des outils pensés pour votre performance
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }, lineHeight: 1.4 }}>
                Tout ce dont vous avez besoin pour gérer et développer votre activité, réuni en une seule plateforme.
              </Typography>
            </Box>
            <Slider {...sliderSettings}>
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.7, x: -100 }}
                  whileInView={{ opacity: 1, scale: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05, rotate: 2 }}
                >
                  <Box sx={{ px: 2, py: { xs: 1, md: 0 } }}>
                    <Card 
                      sx={{ 
                        p: { xs: 2, md: 3 }, 
                        borderRadius: 3, 
                        textAlign: 'left', 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderTop: `4px solid ${feature.color}`,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
                      }}
                    >
                      <motion.div
                        animate={{ 
                          scale: [1, 1.1, 1],
                          rotate: [0, -10, 10, 0]
                        }}
                        transition={{ 
                          duration: 3,
                          repeat: Infinity,
                          repeatType: "reverse",
                          delay: index * 0.4
                        }}
                      >
                        <Box sx={{ mb: 2, color: feature.color }}>
                          {feature.icon}
                        </Box>
                      </motion.div>
                      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' }, lineHeight: 1.3 }}>
                        {feature.title}
                      </Typography>
                      <Typography color="text.secondary" sx={{ flexGrow: 1, fontSize: { xs: '0.9rem', md: '1rem' }, lineHeight: 1.5, wordBreak: 'break-word' }}>
                        {feature.description}
                      </Typography>
                      <Button 
                        endIcon={<ArrowForward />} 
                        onClick={() => navigate('/tarifs')}
                        sx={{ mt: 2, alignSelf: 'flex-start', fontWeight: 'bold' }}
                        color="primary"
                      >
                        En savoir plus
                      </Button>
                    </Card>
                  </Box>
                </motion.div>
              ))}
            </Slider>
          </Container>
        </Box>
      </motion.div>

      {/* SECTION TÉMOIGNAGES */}
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.4 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Box sx={{ py: { xs: 3, md: 10 }, bgcolor: 'grey.50' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 0 } }}>
              <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, lineHeight: 1.2 }}>
                Ce que nos artisans en disent
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }, lineHeight: 1.4 }}>
                Plus de 12,000 artisans nous font confiance pour développer leur activité au quotidien.
              </Typography>
            </Box>
            <Slider {...sliderSettings}>
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.7, rotateX: 45 }}
                  whileInView={{ opacity: 1, scale: 1, rotateX: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05, y: -10 }}
                >
                  <Box sx={{ px: 2 }}>
                    <Card 
                      sx={{ 
                        p: { xs: 2, md: 3 }, 
                        borderRadius: 3, 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <motion.div
                          animate={{ 
                            scale: [1, 1.05, 1],
                            rotate: [0, -3, 3, 0]
                          }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity,
                            repeatType: "reverse",
                            delay: index * 0.5
                          }}
                        >
                          <Avatar src={testimonial.avatar} alt={testimonial.nom} sx={{ width: 56, height: 56, mr: 2 }} />
                        </motion.div>
                        <Box>
                          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' }, lineHeight: 1.3 }}>{testimonial.nom}</Typography>
                          <Typography variant="body2" color="text.secondary">{testimonial.metier}</Typography>
                        </Box>
                      </Box>
                      <motion.div
                        animate={{ 
                          scale: [1, 1.05, 1]
                        }}
                        transition={{ 
                          duration: 2.5,
                          repeat: Infinity,
                          repeatType: "reverse",
                          delay: index * 0.3
                        }}
                      >
                        <Rating value={testimonial.note} readOnly sx={{ mb: 2 }} />
                      </motion.div>
                      <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        "{testimonial.commentaire}"
                      </Typography>
                    </Card>
                  </Box>
                </motion.div>
              ))}
            </Slider>
          </Container>
        </Box>
      </motion.div>

      {/* SECTION MÉTIERS AVEC ACCÈS B2B - OPTIMISÉE LAZY LOADING */}
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.2 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Box sx={{ py: { xs: 3, md: 8 }, bgcolor: 'grey.100' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.3rem', sm: '1.7rem', md: '2.2rem' }, lineHeight: 1.2, mb: 2 }}>
                Accédez directement à la page B2B de votre métier
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
                Découvrez nos solutions spécialisées pour chaque métier. Survolez une carte pour précharger la page.
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {(showAllMetiers ? tousLesMetiers : tousLesMetiers.slice(0, 6)).map((metier, idx) => {
                const slug = getMetierSlug(metier.nom);
                return (
                  <Grid item xs={12} sm={6} md={4} key={metier.nom}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: idx * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.05, y: -5 }}
                    >
                      <Card 
                        ref={(el) => (cardRefs.current[slug] = el)}
                        data-metier-slug={slug}
                        onMouseEnter={() => handleMetierHover(metier.nom)}
                        sx={{ 
                          p: 2, 
                          borderRadius: 3, 
                          boxShadow: 2, 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          minHeight: 200,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: 6,
                            transform: 'translateY(-5px)'
                          }
                        }}
                      >
                        <Avatar sx={{ 
                          bgcolor: metier.gradient || 'primary.main', 
                          background: metier.gradient,
                          width: 64, 
                          height: 64, 
                          mb: 2, 
                          fontSize: 36 
                        }}>
                          {metier.icon}
                        </Avatar>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, textAlign: 'center' }}>
                          {metier.nom}
                        </Typography>
                        {metier.stats && (
                          <Chip 
                            label={metier.stats} 
                            size="small" 
                            color="primary" 
                            sx={{ mb: 1 }} 
                          />
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                          {metier.description}
                        </Typography>
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={() => navigate(`/m/${slug}`)}
                          sx={{ mt: 'auto', fontWeight: 'bold' }}
                          endIcon={<ArrowForward />}
                        >
                          Découvrir
                        </Button>
                      </Card>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
            {tousLesMetiers.length > 6 && (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setShowAllMetiers(!showAllMetiers)}
                  sx={{ fontWeight: 'bold', px: 4 }}
                  endIcon={showAllMetiers ? <ExpandMore sx={{ transform: 'rotate(180deg)' }} /> : <ExpandMore />}
                >
                  {showAllMetiers ? 'Voir moins de métiers' : `Voir tous les métiers (${tousLesMetiers.length})`}
                </Button>
              </Box>
            )}
          </Container>
        </Box>
      </motion.div>
      {/* APPEL À L'ACTION FINAL (CTA) */}
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.5 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Box
          sx={{
            py: { xs: 4, md: 12 },
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)} 0%, ${alpha(theme.palette.secondary.main, 0.95)} 100%)`,
            color: 'white',
            textAlign: 'center',
            px: { xs: 2, md: 0 },
          }}
        >
          <Container maxWidth="md">
            <motion.div
              initial={{ opacity: 0, scale: 0.7, y: 60 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Typography variant="h2" fontWeight="bold" sx={{ mb: 3, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, lineHeight: 1.2 }}>
                Prêt à transformer votre activité ?
              </Typography>
              <Typography variant="h5" sx={{ mb: 5, opacity: 0.9, fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' }, lineHeight: { xs: 1.3, md: 1.5 } }}>
                Rejoignez des milliers d'artisans qui ont choisi LYS pour développer leur business et gagner du temps au quotidien.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                <motion.div 
                  whileHover={{ scale: 1.2, rotate: -5 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/inscription')}
                    sx={{
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      fontWeight: 'bold',
                      px: 5,
                      py: 2,
                      borderRadius: 3,
                      fontSize: '1.1rem',
                      boxShadow: 2,
                      transition: 'all 0.3s',
                      '&:hover': {
                        bgcolor: '#f8fafc',
                        boxShadow: 4
                      }
                    }}
                    startIcon={<Rocket />}
                  >
                    Commencer maintenant
                  </Button>
                </motion.div>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle />
                    <Typography>Gratuit pendant 14 jours</Typography>
                  </Box>
                </motion.div>
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", delay: 0.3 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle />
                    <Typography>Sans engagement</Typography>
                  </Box>
                </motion.div>
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", delay: 0.6 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle />
                    <Typography>Support inclus</Typography>
                  </Box>
                </motion.div>
              </Box>
            </motion.div>
          </Container>
        </Box>
      </motion.div>
    </>
  );
};

export default AccueilComplet;